import React from 'react';
import { Award, Users, Target, Shield, TrendingUp, Heart } from 'lucide-react';

export const About = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative bg-gradient-to-br from-red-600/95 via-red-700/95 to-red-800/95" style={{backgroundImage: 'url(https://images.pexels.com/photos/8793796/pexels-photo-8793796.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-yellow-400">Global Mark Registry</span>
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Your trusted partner in protecting intellectual property and building successful businesses across the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-red-100 px-4 py-2 rounded-full">
                <Target className="w-5 h-5 text-red-600" />
                <span className="text-red-600 font-semibold">Our Mission</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Making Intellectual Property Protection <span className="text-red-600">Accessible</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                At Global Mark Registry, we believe every business—regardless of size—deserves professional trademark protection. For too long, intellectual property services have been expensive, complicated, and intimidating for small business owners and entrepreneurs.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We're changing that. Our mission is to democratize access to high-quality trademark services, making it easy and affordable for anyone to protect their brand. With transparent pricing, expert guidance, and a commitment to client success, we empower businesses to secure their most valuable asset—their brand identity.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-red-600">Core Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do and define who we are as a company.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: 'Integrity First',
                description: 'We operate with complete transparency and honesty. No hidden fees, no false promises—just straightforward, ethical service that puts your interests first.'
              },
              {
                icon: Heart,
                title: 'Client-Centered',
                description: 'Your success is our success. We take time to understand your unique needs and provide personalized solutions, not one-size-fits-all packages.'
              },
              {
                icon: Award,
                title: 'Excellence Always',
                description: 'We maintain the highest standards in everything we do. Our team of experienced trademark attorneys ensures every application receives meticulous attention.'
              },
              {
                icon: TrendingUp,
                title: 'Innovation Driven',
                description: 'We leverage technology and streamlined processes to deliver faster service and better results without compromising quality or personal attention.'
              },
              {
                icon: Users,
                title: 'Accessibility Matters',
                description: 'Professional trademark services shouldn\'t be a luxury. We\'ve designed our services to be affordable and accessible to businesses at every stage.'
              },
              {
                icon: Target,
                title: 'Results Focused',
                description: 'We measure our success by your outcomes. With a 98% approval rate and thousands of successful registrations, we deliver results that matter.'
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our <span className="text-red-600">Approach</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                How we deliver exceptional trademark services that protect your brand and exceed your expectations.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop"
                  alt="Professional approach"
                  className="rounded-2xl shadow-2xl w-full"
                />
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Expert Guidance</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our team of experienced trademark attorneys brings decades of combined experience. We don't just file paperwork—we provide strategic guidance to maximize your protection and minimize risks.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Transparent Process</h3>
                  <p className="text-gray-600 leading-relaxed">
                    You'll never wonder what's happening with your application. We keep you informed at every stage with clear communication, regular updates, and easy access to our support team.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Comprehensive Service</h3>
                  <p className="text-gray-600 leading-relaxed">
                    From initial search to ongoing monitoring, we handle every aspect of trademark protection. One team, one relationship, complete peace of mind.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-12 text-white text-center">
              <h3 className="text-3xl font-bold mb-4">Why Businesses Trust Us</h3>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                We've helped thousands of businesses protect their brands, from solo entrepreneurs to established companies. Our commitment to excellence and client satisfaction sets us apart.
              </p>
              <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <div>
                  <div className="text-5xl font-bold mb-2">10,000+</div>
                  <div className="text-white/90">Trademarks Registered</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">98%</div>
                  <div className="text-white/90">Success Rate</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">24/7</div>
                  <div className="text-white/90">Client Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Our <span className="text-red-600">Commitment</span> to You
              </h2>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  When you choose Global Mark Registry, you're not just getting a service provider—you're gaining a partner invested in your success. We understand that your trademark represents years of hard work, creativity, and investment. That's why we treat every application with the care and attention it deserves.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We're committed to providing the highest level of service at every touchpoint. From your first consultation to ongoing trademark monitoring, we're here to ensure your brand receives the protection it needs to thrive in today's competitive marketplace.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Our promise is simple: professional expertise, transparent pricing, exceptional service, and results you can count on. Your brand's success is our mission, and we won't rest until your intellectual property is fully protected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Protect Your <span className="text-red-600">Brand?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses that trust Global Mark Registry for their trademark protection needs.
          </p>
          <button
            onClick={onOpenPopup}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-xl"
          >
            Get Started Today
          </button>
        </div>
      </section>
    </div>
  );
};